import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import styles from "./style.module.scss";
import { ButtonPrimary } from "@components";
import { useUnit } from "effector-react";
import { $user, userFx } from "../../features/app/model";
import { useTranslation } from "react-i18next";
import $api from "../../http";
import { makeToast } from "../../shared/ui/toast";
import { StealthComparisonGraphic } from './components/StealthComparisonGraphic';

declare global {
    interface Window {
        dataLayer: any[];
        Trustpilot: any;
    }
}

interface UpsellPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    triggerConfetti?: () => void;
}

export const UpsellPopup: React.FC<UpsellPopupProps> = ({ 
    isOpen, 
    onClose,
    onSuccess,
    triggerConfetti
}) => {
    console.log('UpsellPopup rendered with isOpen:', isOpen);
    const user = useUnit($user);
    const { t } = useTranslation();
    
    const [isLoading, setIsLoading] = useState(false);
    const [showDownsell, setShowDownsell] = useState(false);
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAnimate(false);
            const timer = setTimeout(() => setAnimate(true), 10);
            return () => clearTimeout(timer);
        }
    }, [isOpen, showDownsell]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                const trustpilotWidget = document.getElementById("trustpilot-widget-upsell");
                if (window.Trustpilot && trustpilotWidget) {
                    window.Trustpilot.loadFromElement(trustpilotWidget);
                }
            }, 100);
        }
    }, [isOpen]);

    useEffect(() => {
        if (showDownsell) {
            setTimeout(() => {
                const trustpilotWidget = document.getElementById("trustpilot-widget-downsell");
                if (window.Trustpilot && trustpilotWidget) {
                    window.Trustpilot.loadFromElement(trustpilotWidget);
                }
            }, 100);
        }
    }, [showDownsell]);

    const handleUpsellPurchase = async () => {
        if (!user) {
            window.location.href = "/auth";
            return;
        }

        setIsLoading(true);

        try {
            ReactGA.event({
                category: "Upsell",
                action: "Purchase",
                label: "Stealth Model Upsell - $29.99",
                value: 29.99,
            });

            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'upsell_purchase_attempt',
                    value: 29.99,
                    currency: 'USD',
                    item_name: 'Stealth Model Upsell',
                    user_email: user?.email,
                });
            }

            console.log("Upsell purchase initiated. Awaiting Subscription ID.");
            const response = await $api.api.upsellControllerUpsell();

            if (response.data.success) {
                ReactGA.event({
                    category: "Upsell",
                    action: "Purchase_Success",
                    label: "Stealth Model Upsell - $29.99",
                    value: 29.99,
                });

                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'upsell_purchase_success',
                        value: 29.99,
                        currency: 'USD',
                        item_name: 'Stealth Model Upsell',
                        user_email: user?.email,
                    });
                    window.dataLayer.push({
                        event: 'ads_upsell',
                        value: 29.99,
                        currency: 'USD',
                        item_name: 'Stealth Model Upsell',
                        user_email: user?.email,
                    });
                }

                localStorage.removeItem('upsell_declined');
                
                await userFx();

                makeToast("success", t("upsell.purchase.success") || "Stealth Model activated successfully!");
                triggerConfetti?.();
                onSuccess?.();
                onClose();
            } else {
                throw new Error("Purchase was not successful according to the server.");
            }
        } catch (error) {
            console.error("Upsell purchase error:", error);
            
            ReactGA.event({
                category: "Upsell",
                action: "Purchase_Error",
                label: "Stealth Model Upsell Failed",
            });

            makeToast("error", t("upsell.purchase.error") || "Failed to purchase Stealth Model. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownsellPurchase = async () => {
        if (!user) {
            window.location.href = "/auth";
            return;
        }

        setIsLoading(true);

        try {
            ReactGA.event({
                category: "Upsell",
                action: "Purchase",
                label: "Stealth Model Downsell - $19.99",
                value: 19.99,
            });

            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'downsell_purchase_attempt',
                    value: 19.99,
                    currency: 'USD',
                    item_name: 'Stealth Model Downsell',
                    user_email: user?.email,
                });
            }

            console.log("Downsell purchase initiated. Awaiting Subscription ID.");
            const response = await $api.api.upsellControllerDownsell();

            if (response.data.success) {
                ReactGA.event({
                    category: "Upsell",
                    action: "Purchase_Success",
                    label: "Stealth Model Downsell - $19.99",
                    value: 19.99,
                });

                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'downsell_purchase_success',
                        value: 19.99,
                        currency: 'USD',
                        item_name: 'Stealth Model Downsell',
                        user_email: user?.email,
                    });
                    window.dataLayer.push({
                        event: 'ads_upsell',
                        value: 19.99,
                        currency: 'USD',
                        item_name: 'Stealth Model Downsell',
                        user_email: user?.email,
                    });
                }

                localStorage.removeItem('upsell_declined');
                
                await userFx();
                
                makeToast("success", t("upsell.purchase.success") || "Stealth Model activated successfully!");
                triggerConfetti?.();
                onSuccess?.();
                onClose();
            } else {
                throw new Error("Purchase was not successful according to the server.");
            }
        } catch (error) {
            console.error("Downsell purchase error:", error);
            
            ReactGA.event({
                category: "Upsell",
                action: "Purchase_Error",
                label: "Stealth Model Downsell Failed",
            });

            makeToast("error", t("upsell.purchase.error") || "Failed to purchase Stealth Model. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpsellDecline = () => {
        ReactGA.event({
            category: "Upsell",
            action: "Decline",
            label: "Stealth Model Upsell Declined",
        });

        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'upsell_declined',
                popup_type: 'stealth_model_upsell',
                user_email: user?.email,
            });
        }

        setShowDownsell(true);
    };

    const handleDownsellDecline = () => {
        ReactGA.event({
            category: "Upsell",
            action: "Decline",
            label: "Stealth Model Downsell Declined",
        });

        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'downsell_declined',
                popup_type: 'stealth_model_downsell',
                user_email: user?.email,
            });
        }

        localStorage.setItem('upsell_declined', 'true');

        setShowDownsell(false);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            if (showDownsell) {
                handleDownsellDecline();
            } else {
                handleUpsellDecline();
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.UpsellPopup} onClick={handleBackdropClick}>
            <div className={styles.UpsellPopup__modal}>
                <button className={styles.UpsellPopup__closeButton} onClick={() => showDownsell ? handleDownsellDecline() : handleUpsellDecline()}>
                    ×
                </button>
                
                {!showDownsell ? (
                    // Upsell Offer ($29.99)
                    <>
                        <div className={styles.UpsellPopup__header}>
                            <h2 className={styles.UpsellPopup__title}>
                                {t("upsell.title") || "Add"} <span className={styles.UpsellPopup__highlight}>Stealth Model</span> {t("upsell.title2") || "To Your Plan!"}
                            </h2>
                        </div>

                        <div className={styles.UpsellPopup__mainContent}>
                            <div className={styles.UpsellPopup__badge}>
                                {t("upsell.badge") || "Limited Offer"}
                            </div>
                            <div className={styles.UpsellPopup__leftColumn}>
                                <div className={styles.UpsellPopup__priceSection}>
                                    <span className={styles.UpsellPopup__offerTitle}>
                                        <span className={styles.UpsellPopup__offerTitleLine}>Game Over for</span>
                                        <span className={styles.UpsellPopup__offerTitleLine}>AI Detectors</span>
                                    </span>
                                    <div className={styles.UpsellPopup__priceDetails}>
                                        <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__originalPrice}>$39.99</span>
                                        </div>
                                        <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__currentPrice}>$29.99</span>
                                        </div>
                                        <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__priceTenure}>
                                                {t("upsell.lifetime", "Lifetime")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={`${styles.UpsellPopup__features} ${animate ? styles.animate : ''}`}>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature1") || "Bypass recent GPTZero & Turnitin updates"}</span>
                                    </div>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature2") || "Advanced text humanization algorithms"}</span>
                                    </div>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature3") || "Lifetime access - pay once, use forever"}</span>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.UpsellPopup__rightColumn}>
                                <StealthComparisonGraphic />
                            </div>
                        </div>

                        <div className={styles.UpsellPopup__footer}>
                            <div className={styles.UpsellPopup__trustpilot}>
                                <div
                                    id="trustpilot-widget-upsell"
                                    className="trustpilot-widget"
                                    data-locale="en-US"
                                    data-template-id="5419b732fbfb950b10de65e5"
                                    data-businessunit-id="672aaad52b5b53262f0a57d8"
                                    data-style-height="24px"
                                    data-style-width="100%"
                                    data-token="d24220d3-5896-4329-b922-ec1d2d84aff8"
                                >
                                    <a
                                        href="https://www.trustpilot.com/review/humanizeai.now"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Trustpilot
                                    </a>
                                </div>
                                <div className={styles.UpsellPopup__trustpilotMeta}>
                                    {t("upsell.basedOn") || "Based on"} 6,583+ {t("upsell.verified") || "verified reviews"}
                                </div>
                            </div>
                            <div className={styles.UpsellPopup__buttonSection}>
                                <ButtonPrimary
                                    onClick={handleUpsellPurchase}
                                    disabled={isLoading}
                                    colorScheme="orange"
                                    textAlign="center"
                                    className={styles.UpsellPopup__payButton}
                                >
                                    {isLoading ? "Processing..." : (t("upsell.getStealthModel") || "Add Stealth Model")}
                                </ButtonPrimary>
                                <button 
                                    className={styles.UpsellPopup__skipButton}
                                    onClick={handleUpsellDecline}
                                    disabled={isLoading}
                                >
                                    {t("upsell.skipAndContinue") || "Skip and Continue"}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    // Downsell Offer ($19.99)
                    <>
                         <div className={styles.UpsellPopup__header}>
                            <h2 className={`${styles.UpsellPopup__title} ${styles.UpsellPopup__title_downsell}`}>
                                {t("downsell.title") || "Last chance for"} <span className={styles.UpsellPopup__highlight}>Stealth Model</span>
                            </h2>
                        </div>

                        <div className={styles.UpsellPopup__mainContent}>
                             <div className={styles.UpsellPopup__badge}>
                                {t("downsell.badge") || "Last Chance"}
                            </div>
                            <div className={styles.UpsellPopup__leftColumn}>
                                <div className={styles.UpsellPopup__priceSection}>
                                    <span className={styles.UpsellPopup__offerTitle}>
                                        <span className={styles.UpsellPopup__offerTitleLine}>Game Over for</span>
                                        <span className={styles.UpsellPopup__offerTitleLine}>AI Detectors</span>
                                    </span>
                                    <div className={styles.UpsellPopup__priceDetails}>
                                       <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__originalPrice}>$39.99</span>
                                        </div>
                                        <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__currentPrice}>$19.99</span>
                                        </div>
                                        <div className={styles.UpsellPopup__priceRow}>
                                            <span className={styles.UpsellPopup__priceTenure}>
                                                {t("upsell.lifetime", "Lifetime")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={`${styles.UpsellPopup__features} ${animate ? styles.animate : ''}`}>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature1") || "Bypass recent GPTZero & Turnitin updates"}</span>
                                    </div>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature2") || "Advanced text humanization algorithms"}</span>
                                    </div>
                                    <div className={styles.UpsellPopup__feature}>
                                        <span className={styles.UpsellPopup__checkmark}>✓</span>
                                        <span>{t("upsell.feature3") || "Lifetime access - pay once, use forever"}</span>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.UpsellPopup__rightColumn}>
                               <StealthComparisonGraphic />
                            </div>
                        </div>
                        
                        <div className={styles.UpsellPopup__footer}>
                            <div className={styles.UpsellPopup__trustpilot}>
                                <div
                                    id="trustpilot-widget-downsell"
                                    className="trustpilot-widget"
                                    data-locale="en-US"
                                    data-template-id="5419b732fbfb950b10de65e5"
                                    data-businessunit-id="672aaad52b5b53262f0a57d8"
                                    data-style-height="24px"
                                    data-style-width="100%"
                                    data-token="d24220d3-5896-4329-b922-ec1d2d84aff8"
                                >
                                    <a
                                        href="https://www.trustpilot.com/review/humanizeai.now"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Trustpilot
                                    </a>
                                </div>
                                <div className={styles.UpsellPopup__trustpilotMeta}>
                                    {t("upsell.basedOn") || "Based on"} 6,583+ {t("upsell.verified") || "verified reviews"}
                                </div>
                            </div>
                            <div className={styles.UpsellPopup__urgency}>
                                <span className={styles.UpsellPopup__urgencyText}>
                                    🔥 {t("downsell.urgency") || "This 50% discount expires in 5 minutes!"}
                                </span>
                            </div>
                            <div className={styles.UpsellPopup__buttonSection}>
                                <ButtonPrimary
                                    onClick={handleDownsellPurchase}
                                    disabled={isLoading}
                                    colorScheme="orange"
                                    textAlign="center"
                                    className={styles.UpsellPopup__payButton}
                                >
                                    {isLoading ? "Processing..." : ( <><span className={styles.emoji}>⚡</span>{t("downsell.getStealthModel") || "Get Stealth Model (50% OFF)"}</>)}
                                </ButtonPrimary>
                                <button 
                                    className={styles.UpsellPopup__skipButton}
                                    onClick={handleDownsellDecline}
                                    disabled={isLoading}
                                >
                                    {t("downsell.decline") || "No thanks, I don't want lifetime Stealth Model access"}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
